// 유닛 능력치 — docs/GAME_DESIGN.md 3·4장 (기본 7종 + 맹세의 궁극 유닛 3종 + 해군 3종 + 공군 3종)
// 사거리·이동 속도·반지름·시야는 타일 단위, 생산 시간·공격 간격은 초 단위

export const UNITS = Object.freeze({
  peasant: {
    id: 'peasant',
    name: '농노',
    age: 1,
    producedAt: 'keep',
    cost: { gold: 50, wood: 0, mana: 0 },
    trainTime: 12,
    pop: 1,
    hp: 60,
    armor: 'light',
    speed: 2.5,
    radius: 0.3,
    sight: 6,
    attack: { damage: 5, type: 'normal', range: 1, cooldown: 1.5 },
    worker: true,
  },
  pikeman: {
    id: 'pikeman',
    name: '창병',
    age: 1,
    producedAt: 'barracks',
    cost: { gold: 35, wood: 25, mana: 0 },
    trainTime: 18,
    pop: 1,
    hp: 110,
    armor: 'light',
    speed: 2.4,
    radius: 0.32,
    sight: 7,
    attack: { damage: 9, type: 'normal', range: 1, cooldown: 1.2 },
    bonusVsTag: { cavalry: 3 },
  },
  longbowman: {
    id: 'longbowman',
    name: '장궁병',
    age: 1,
    producedAt: 'barracks',
    cost: { gold: 30, wood: 45, mana: 0 },
    trainTime: 20,
    pop: 1,
    hp: 70,
    armor: 'light',
    speed: 2.4,
    radius: 0.3,
    sight: 8,
    attack: { damage: 12, type: 'pierce', range: 6, cooldown: 1.6 },
  },
  scout_rider: {
    id: 'scout_rider',
    name: '척후 기병',
    age: 1,
    producedAt: 'stables',
    cost: { gold: 55, wood: 15, mana: 0 },
    trainTime: 20,
    pop: 1,
    hp: 120,
    armor: 'light',
    speed: 4.2,
    radius: 0.38,
    sight: 10,
    attack: { damage: 8, type: 'normal', range: 1, cooldown: 1.2 },
    tags: ['cavalry'],
    bonusVsBuilding: { obelisk: 3, farmstead: 3 },
  },
  knight: {
    id: 'knight',
    name: '기사',
    age: 2,
    producedAt: 'stables',
    cost: { gold: 90, wood: 40, mana: 0 },
    trainTime: 28,
    pop: 2,
    hp: 260,
    armor: 'heavy',
    speed: 3.6,
    radius: 0.4,
    sight: 7,
    attack: { damage: 18, type: 'normal', range: 1, cooldown: 1.4 },
    tags: ['cavalry'],
  },
  royal_guard: {
    id: 'royal_guard',
    name: '왕실 근위병',
    age: 2,
    producedAt: 'barracks',
    cost: { gold: 70, wood: 20, mana: 0 },
    trainTime: 26,
    pop: 2,
    hp: 240,
    armor: 'heavy',
    speed: 2.1,
    radius: 0.36,
    sight: 7,
    attack: { damage: 14, type: 'normal', range: 1, cooldown: 1.3 },
    ability: 'shieldWall',
  },
  battlemage: {
    id: 'battlemage',
    name: '전투 마법사',
    age: 2,
    producedAt: 'mage_tower',
    cost: { gold: 60, wood: 30, mana: 60 },
    trainTime: 30,
    pop: 2,
    hp: 90,
    armor: 'light',
    speed: 2.2,
    radius: 0.3,
    sight: 8,
    attack: { damage: 20, type: 'magic', range: 5, cooldown: 2.0, splash: 1.2 },
  },

  // ---------- 맹세의 궁극 유닛 (왕국 시대, 맹세의 성소) ----------
  // 한 경기에 한 기만 존재한다. oath가 맞는 플레이어만 뽑을 수 있다.

  solarion: {
    id: 'solarion',
    name: '솔라리온',
    title: '새벽의 성기사왕',
    age: 3,
    producedAt: 'sanctum',
    oath: 'crown',
    ultimate: true,
    cost: { gold: 800, wood: 300, mana: 400 },
    trainTime: 90,
    pop: 6,
    hp: 1800,
    armor: 'heavy',
    speed: 3.0,
    radius: 0.55,
    sight: 9,
    attack: { damage: 60, type: 'normal', range: 1, cooldown: 1.2 },
    tags: ['cavalry'],
    bonusVsArmor: { colossal: 3 }, // 거인 사냥꾼
    abilities: ['dawnCharge'],
    aura: { radius: 6, damage: 1.2, resist: 0.85 }, // 새벽의 오라
    revive: { delay: 60, cost: { gold: 300, wood: 0, mana: 0 } }, // 불멸의 맹세
  },
  etheria: {
    id: 'etheria',
    name: '에테리아',
    title: '별을 엮는 대마법사',
    age: 3,
    producedAt: 'sanctum',
    oath: 'rune',
    ultimate: true,
    cost: { gold: 400, wood: 200, mana: 900 },
    trainTime: 90,
    pop: 6,
    hp: 900,
    armor: 'light',
    speed: 2.2,
    radius: 0.42,
    sight: 10,
    attack: { damage: 40, type: 'magic', range: 7, cooldown: 1.8, chain: { targets: 3, falloff: 0.25 } },
    abilities: ['starfall', 'timeWard'],
    manaResonance: { perObelisk: 0.1, max: 0.4 }, // 마나 샘 1곳당 재사용 대기시간 -10%
  },
  arkanon: {
    id: 'arkanon',
    name: '아르카논',
    title: '성채를 짊어진 신수',
    age: 3,
    producedAt: 'sanctum',
    oath: 'earth',
    ultimate: true,
    cost: { gold: 600, wood: 600, mana: 600 },
    trainTime: 120,
    pop: 10,
    hp: 5000,
    armor: 'colossal',
    speed: 1.4,
    radius: 0.7,
    sight: 10,
    attack: { damage: 70, type: 'normal', range: 1.5, cooldown: 2.2, splash: 2 },
    abilities: ['root', 'unload'],
    // 등 위의 성채: 원거리 유닛은 등 위에서 사거리 +2로 싸우고, 아르카논이 쓰러져도 살아 내린다
    garrison: {
      capacity: 6,
      riders: ['pikeman', 'longbowman', 'royal_guard', 'battlemage'],
      rangeBonus: 2,
      ridersFight: true,
      sinks: false,
    },
  },

  // ---------- 해군 (조선소) ----------
  // naval: 물과 다리 밑으로만 다닌다. 근접 공격은 배에 닿지 않는다 (rules/combat.js)
  // 순서를 바꾸면 스냅샷의 유닛 번호가 달라지므로 새 유닛은 늘 맨 뒤에 붙인다

  war_galley: {
    id: 'war_galley',
    name: '전투 갤리',
    title: '화살을 퍼붓는 돛배',
    age: 1,
    producedAt: 'shipyard',
    cost: { gold: 60, wood: 100, mana: 0 },
    trainTime: 30,
    pop: 2,
    hp: 320,
    armor: 'naval',
    speed: 3.2,
    radius: 0.6,
    sight: 9,
    attack: { damage: 13, type: 'pierce', range: 6, cooldown: 1.6 },
    naval: true,
  },
  transport_ship: {
    id: 'transport_ship',
    name: '수송선',
    title: '병력을 실어 나르는 배',
    age: 1,
    producedAt: 'shipyard',
    cost: { gold: 0, wood: 120, mana: 0 },
    trainTime: 24,
    pop: 1,
    hp: 360,
    armor: 'naval',
    speed: 3.4,
    radius: 0.65,
    sight: 7,
    attack: null, // 싸우지 않는다
    naval: true,
    abilities: ['unload'],
    // 궁극 유닛이 아닌 뭍 유닛은 누구나 탄다. 탄 유닛은 싸우지 않고, 배가 가라앉으면 함께 잃는다
    garrison: { capacity: 8, riders: 'land', rangeBonus: 0, ridersFight: false, sinks: true },
  },
  catapult_ship: {
    id: 'catapult_ship',
    name: '투석 전함',
    title: '물가의 건물을 부수는 배',
    age: 2,
    producedAt: 'shipyard',
    cost: { gold: 140, wood: 180, mana: 0 },
    trainTime: 40,
    pop: 3,
    hp: 450,
    armor: 'naval',
    speed: 2.3,
    radius: 0.7,
    sight: 9,
    attack: { damage: 40, type: 'siege', range: 8, cooldown: 3.8, splash: 1 },
    naval: true,
  },

  // ---------- 공군 (그리폰 둥지) ----------
  // flying: 지형과 유닛을 무시하고 곧장 난다. 근접 공격은 하늘에 닿지 않고, 관통 피해를 두 배로 받는다

  falcon_scout: {
    id: 'falcon_scout',
    name: '매 정찰병',
    title: '멀리 보는 눈',
    age: 2,
    producedAt: 'aerie',
    cost: { gold: 40, wood: 0, mana: 20 },
    trainTime: 14,
    pop: 1,
    hp: 90,
    armor: 'air',
    speed: 5.5,
    radius: 0.3,
    sight: 13,
    attack: null, // 싸우지 않는다
    flying: true,
  },
  gryphon_rider: {
    id: 'gryphon_rider',
    name: '그리폰 기수',
    title: '하늘에서 덮치는 기사',
    age: 3,
    producedAt: 'aerie',
    cost: { gold: 120, wood: 60, mana: 80 },
    trainTime: 35,
    pop: 3,
    hp: 300,
    armor: 'air',
    speed: 4.0,
    radius: 0.45,
    sight: 10,
    attack: { damage: 22, type: 'normal', range: 2, cooldown: 1.3 }, // 내리찍기 — 하늘도 때린다
    flying: true,
  },
  storm_wyvern: {
    id: 'storm_wyvern',
    name: '폭풍 비룡',
    title: '성벽을 부수는 날개',
    age: 3,
    producedAt: 'aerie',
    cost: { gold: 160, wood: 80, mana: 160 },
    trainTime: 45,
    pop: 4,
    hp: 420,
    armor: 'air',
    speed: 3.2,
    radius: 0.6,
    sight: 10,
    attack: { damage: 45, type: 'siege', range: 2.5, cooldown: 3.2, splash: 1.2 }, // 공성이라 하늘은 못 때린다
    flying: true,
  },
});

export const isNaval = (type) => Boolean(UNITS[type]?.naval);
export const isFlying = (type) => Boolean(UNITS[type]?.flying);

/** 유닛이 다니는 곳: 'land' | 'water' | 'air'. 서로 다른 곳을 다니면 부딪히지 않는다 */
export const domainOf = (type) => (UNITS[type]?.flying ? 'air' : UNITS[type]?.naval ? 'water' : 'land');

/** 궁극 유닛인지 */
export const isUltimate = (type) => Boolean(UNITS[type]?.ultimate);

/** 왕실 근위병의 방패벽: 켜면 느려지는 대신 화살(관통 피해)에 강해진다 */
export const SHIELD_WALL = Object.freeze({ speedMultiplier: 0.5, pierceTakenMultiplier: 0.4 });

/** 농노의 채집 능력 */
export const WORKER = Object.freeze({
  carryCapacity: 10,
  gatherRate: { gold: 1.25, wood: 1.0 },
});

/** 스냅샷에서 유닛 종류를 숫자로 보낼 때 쓰는 순서. 순서를 바꾸면 서버와 클라이언트를 함께 배포해야 한다. */
export const UNIT_TYPES = Object.freeze(Object.keys(UNITS));
